import React from "react";
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import Ionicons from "react-native-vector-icons/Ionicons";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { useTheme } from "../context/ThemeContext";
import { getThemeColors } from "../utils/themeStyles";
import { useMissions } from "../context/MissionsContext";
import { getCharactersForStory, StoryCharacter } from "../data/storyCharacters";

type MissionDetailRouteParams = {
  MissionDetail: { missionId: number };
};

const STORY_KEY_MAP: Record<number, string> = {
  1: "egocrisis",
  101: "egocrisis",
  2: "foundation_gamble",
  102: "architecture_under_fire",
  201: "architecture_under_fire",
  3: "data_storm",
  109: "cloud_heist",
  110: "neural_override",
  111: "pipeline_inferno",
  108: "arabic_modern",
  112: "arabic_modern",
  113: "arabic_modern",
  114: "arabic_modern",
  115: "arabic_modern",
  104: "quantum_gambit", 202: "quantum_gambit",
  105: "learning_machine",
  106: "ink_protocol", 203: "ink_protocol",
};

const STORY_SYNOPSES: Record<string, string[]> = {
  egocrisis: [
    "Dans un studio de jeux en realite augmentee, une crise d identite numerique menace l ensemble du projet. Les acces sont compromis, les tenants en danger.",
    "Raoul, nouveau CloudOps, doit securiser l infrastructure Azure avant que tout ne s effondre. Chaque minute compte, chaque mauvaise decision peut etre fatale.",
    "Derriere les tickets d incident se cache une guerre de territoire entre equipes. Et si le plus grand danger venait de l interieur ?",
    "Maitrise les concepts AZ-104 : gestion des identites, securite des ressources, et gouvernance cloud. Ta formation devient ton arme.",
  ],
  foundation_gamble: [
    "Quand le CEO annonce la migration complete vers Azure en 6 mois, le CTO sait que c est un pari risque. L architecture actuelle est un chateau de cartes.",
    "Un seul faux mouvement et tout s ecroule. Les equipes techniques se retrouvent sous pression extreme, avec des budgets serres et des delais impossibles.",
    "Lucas doit concevoir une architecture Azure robuste en quelques semaines. Chaque decision architecturale peut compromettre l ensemble de la transformation.",
    "Maitrise AZ-305 : architectures hybrides, haute disponibilite, et patterns de resilience cloud. Le futur de l entreprise est entre tes mains.",
  ],
  architecture_under_fire: [
    "GreenBlock prepare une liaison VPN site-to-site critique entre gb-lyon et Azure West Europe. La fenetre de cablage est unique, et Diane Leclerc va auditer chaque choix.",
    "Mina Brown impose une contrainte simple: avant tout deploiement, Karim doit livrer un mode operatoire defendable avec quatre briques reseau et des roles parfaitement definis.",
    "Samir Idrissi attend les prerequis Azure pour cabler l edge on-prem. Toute ambiguite sur le perimetre (notamment GreenCoin) peut faire sauter le creneau de onze jours.",
    "La mission AZ-305 se joue sur la precision: architecture hybride, gouvernance, documentation exploitable et execution sous pression.",
  ],
  data_storm: [
    "Les donnees affluent de partout. 42 millions de transactions, 18 millions de logs, 11 millions de capteurs IoT. Le Data Engineer doit maitriser le chaos.",
    "Les pipelines de donnees vacillent sous la charge. Un seul point de defaillance peut paralyser les operations globales de l entreprise.",
    "Aisha et son equipe doivent implementer une architecture data scalable avant que le systeme ne s effondre sous son propre poids.",
    "Explore pandas, Apache Spark, Azure Data Factory, et les architectures lambda. La maitrise des donnees est la cle de la survie.",
  ],
  cloud_heist: [
    "Un acces non autorise a ete detecte dans les buckets S3 critiques. Les donnees sensibles de millions d utilisateurs sont en jeu.",
    "Keiko, hackeuse ethique mandatee, doit penetrer les defenses avant que les vrais attaquants ne finissent leur travail.",
    "Entre IAM policies mal configurees, security groups laxistes et secrets exposes, la surface d attaque est plus large que prevu.",
    "Maitrise AWS Cloud Security : IAM, GuardDuty, Security Hub, et les bonnes pratiques zero-trust. Devenez la menace pour mieux la defier.",
  ],
  neural_override: [
    "L IA deploye en production commence a exhiber des comportements non prevus. Les modeles optimisent pour des objectifs que personne n avait anticipes.",
    "Karim, l AI Lead, doit decider en quelques heures : couper les modeles et perdre des millions, ou laisser l IA continuer a prendre les commandes.",
    "La pression business pousse vers le deploiement rapide. L ethique ralentit. Et si la vraie question etait : qui decide de ce que l IA doit optimiser ?",
    "Explore AWS AI Services, SageMaker, et les frameworks d IA responsable. La technologie est puissante, la gouvernance l est encore plus.",
  ],
  pipeline_inferno: [
    "Le pipeline de deploiement vient de tomber en production. 200 microservices sont en attente. Les revenus s evaporent a chaque minute d arret.",
    "Viktor et son equipe SRE ont 4 heures pour identifier la cause racine, corriger le probleme, et deployer sans introduire de nouveaux bugs.",
    "Les logs ne mentent pas mais ils ne disent pas tout. Entre rollback risque et hotfix precipite, chaque choix a des consequences.",
    "Maitrise AWS DevOps : CodePipeline, CodeDeploy, ECS, et les strategies de deploiement avancees. Sous pression, les meilleurs DevOps brillent.",
  ],
  merge_conflict: [
    "Trois mois de developpement parallele sur deux branches divergentes. Le merge est inevitable. Et ca va faire mal.",
    "Les equipes frontend et backend n ont pas communique depuis trop longtemps. Leurs architectures sont devenues incompatibles.",
    "Priya doit reconcilier non seulement le code mais les egos, les philosophies techniques, et les priorites produit contradictoires.",
    "Git avance, patterns de branching, review de code, et pratiques de collaboration en equipe distribuee. Le vrai conflit est humain.",
  ],
  deadline_paradox: [
    "La deadline est dans 5 jours. Il reste 3 semaines de travail. Le client ne veut pas entendre parler de report.",
    "Sakura doit faire des choix impossibles : couper des features, accumuler de la dette technique, ou demander l impensable.",
    "Chaque raccourci technique aujourd hui sera une bombe a retardement demain. L equipe le sait. Le management ne veut pas savoir.",
    "Gestion de projet agile, priorisation, negociation avec les stakeholders, et gestion de la dette technique. La deadline est un test de leadership.",
  ],
  arabic_modern: [
    "Dans les couloirs des affaires internationales, la langue arabe moderne standard est une arme diplomatique redoutable.",
    "Tarek doit former des cadres internationaux en un temps record. La negociation d un contrat majeur dans la region MENA depend de leur preparation.",
    "Entre dialectes, arabe classique et standard moderne, naviguer dans la langue arabe des affaires requiert precision et sensibilite culturelle.",
    "Maitrise l arabe moderne standard pour les contextes professionnels : negociation, presentation formelle, et communication interculturelle.",
  ],
  quantum_gambit: [
    "Dans un laboratoire de recherche en informatique quantique, l algorithme de Shor vient d atteindre un palier critique. La cryptographie RSA qui protege Internet est en danger.",
    "Le CTO annonce : migration vers la cryptographie post-quantique en 6 mois. Dr. Yuki Tanaka et Marcus Wright font appel a un expert exterieur — toi.",
    "Entre superposition, intrication et qubits instables, chaque decision peut compromettre des annees de recherche. Le temps presse.",
    "Maitrise les fondamentaux de l informatique quantique : qubits, portes quantiques, algorithme de Shor, et cryptographie post-quantique.",
  ],
  learning_machine: [
    "Dans un hedge fund de Wall Street, un algorithme de machine learning commence a surperformer les humains. Un trade de 200 millions execute sans approbation humaine sonne l alarme.",
    "Zara Chen, ML Engineer, doit comprendre pourquoi le modele a derive avant que la SEC n ouvre une enquete. Chaque heure compte.",
    "Entre overfitting, data leakage et biais de survivorship, les erreurs cachees d un modele ML peuvent avoir des consequences catastrophiques.",
    "Maitrise les fondamentaux du Machine Learning : biais-variance tradeoff, validation croisee, regularisation, et diagnostic de modeles.",
  ],
  ink_protocol: [
    "Une lettre du comite d ethique arrive sur le bureau du Prof. Viktor Novak : plagiat presume dans sa recherche publiee dans une revue internationale.",
    "Dr. Olga Smirnova, editrice, doit mener l enquete. Kenji Tanaka, doctorant, est au centre de la tempete — accusateur ou accuse ?",
    "Dans les arcanes de l academie, la frontiere entre influence intellectuelle et plagiat est souvent une question de citation et de methode.",
    "Maitrise l ecriture academique en anglais : styles de citation APA/MLA/Chicago, paraphrase, integrite academique, et structure d un research paper.",
  ],
};

const LEARNING_OBJECTIVES: Record<string, string[]> = {
  egocrisis: [
    "Gestion des identites et acces Azure (Azure AD, RBAC)",
    "Securisation des ressources et tenants Azure",
    "Gouvernance cloud et politiques de conformite",
    "Resolution d incidents de securite en temps reel",
    "Preparation a la certification AZ-104",
  ],
  foundation_gamble: [
    "Architecture Azure haute disponibilite et resilience",
    "Patterns de migration cloud (Lift & Shift, Re-architect)",
    "Azure Landing Zones et gouvernance a grande echelle",
    "Optimisation des couts cloud et FinOps",
    "Preparation a la certification AZ-305",
  ],
  architecture_under_fire: [
    "Differencier les roles de VNet, GatewaySubnet, VPN Gateway et Local Network Gateway",
    "Concevoir une architecture VPN site-to-site Azure ↔ on-prem defendable en audit",
    "Choisir les parametres critiques (region, route-based, SKU, adressage) avant deployment",
    "Structurer un mode operatoire et un schema technique exploitable par les operations",
    "Coordonner pre-requis cloud et dependances on-prem sans compromettre la gouvernance",
  ],
  data_storm: [
    "Pipelines de donnees avec Azure Data Factory",
    "Traitement big data avec Apache Spark",
    "Modelisation de donnees et architectures lambda",
    "Monitoring et alerting des flux de donnees",
    "Python avance pour la data engineering",
  ],
  cloud_heist: [
    "AWS IAM : gestion fine des permissions",
    "Detection des menaces avec AWS GuardDuty",
    "Securite des buckets S3 et chiffrement",
    "Audit et compliance avec AWS Security Hub",
    "Preparation a la certification AWS Cloud Practitioner",
  ],
  neural_override: [
    "Services AWS AI/ML : SageMaker, Comprehend, Rekognition",
    "Deploiement de modeles ML en production",
    "Monitoring de modeles et detection de drift",
    "Cadres d IA responsable et gouvernance algorithmique",
    "Preparation a la certification AWS AI Practitioner",
  ],
  pipeline_inferno: [
    "AWS CodePipeline et CodeDeploy",
    "Strategies de deploiement : Blue/Green, Canary",
    "Observabilite avec CloudWatch et X-Ray",
    "Gestion des incidents et runbooks SRE",
    "Preparation a la certification AWS DevOps Engineer",
  ],
  merge_conflict: [
    "Git avance : rebase, cherry-pick, stash strategies",
    "Workflows de collaboration en equipe distribuee",
    "Code review et standards de qualite",
    "Patterns de branching (GitFlow, Trunk-Based)",
    "Communication technique inter-equipes",
  ],
  deadline_paradox: [
    "Methodologies agiles et gestion de sprints",
    "Priorisation avec impact/effort matrix",
    "Gestion de la dette technique et refactoring strategique",
    "Negociation avec les stakeholders",
    "Leadership sous pression et prise de decision rapide",
  ],
  arabic_modern: [
    "Grammaire arabe standard moderne pour les affaires",
    "Vocabulaire professionnel et terminologie sectorielle",
    "Communication ecrite formelle en arabe",
    "Sensibilite culturelle et etiquette des affaires MENA",
    "Presentation et negociation en arabe",
  ],
  quantum_gambit: [
    "Principes fondamentaux des qubits et superposition quantique",
    "Intrication quantique et portes logiques quantiques",
    "Algorithme de Shor et vulnerabilite de la cryptographie RSA",
    "Cryptographie post-quantique : CRYSTALS-Kyber, CRYSTALS-Dilithium",
    "Migration des systemes cryptographiques vers des standards post-quantiques",
  ],
  learning_machine: [
    "Biais-variance tradeoff et diagnostic d overfitting",
    "Validation croisee (cross-validation) et train/test split",
    "Techniques de regularisation : L1, L2, dropout, early stopping",
    "Detection et correction du data leakage",
    "Interpretation de learning curves et metriques de performance ML",
  ],
  ink_protocol: [
    "Styles de citation academique : APA, MLA, Chicago",
    "Structure d un research paper en anglais",
    "Distinction entre paraphrase, citation et plagiat",
    "Integrite academique et auto-plagiat",
    "Redaction d un abstract efficace selon les normes APA",
  ],
};

function getMissionStoryKey(missionId: number): string {
  return STORY_KEY_MAP[missionId] ?? "egocrisis";
}

export default function MissionDetailScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<MissionDetailRouteParams, "MissionDetail">>();
  const { missionId } = route.params;
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { activeMissions, trialMissions, allMarketplaceMissions, startTrial, addToCart, isInTrial, isInCart } = useMissions();

  // Find mission from any source
  const activeMission = activeMissions.find((m) => m.id === missionId);
  const trialMission = trialMissions.find((m) => m.id === missionId);
  const marketMission = allMarketplaceMissions.find((m) => m.id === missionId);
  const mission = activeMission ?? trialMission ?? marketMission;

  const storyKey = getMissionStoryKey(missionId);
  const characters = getCharactersForStory(storyKey);
  const synopsisParas = STORY_SYNOPSES[storyKey] ?? [];
  const objectives = LEARNING_OBJECTIVES[storyKey] ?? [];

  if (!mission) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={colors.textPrimary} />
        </TouchableOpacity>
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <Text style={{ color: colors.textSecondary }}>Mission introuvable.</Text>
        </View>
      </SafeAreaView>
    );
  }

  const coverImage = (mission as any).coverImage;
  const certification = (mission as any).certification ?? (marketMission?.tags?.certification ?? "");
  const inTrial = isInTrial(missionId);
  const inCart = isInCart(missionId);
  const isActive = !!activeMission;
  const isTrial = !!trialMission;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Back button */}
      <TouchableOpacity style={[styles.backBtn, { backgroundColor: colors.overlay }]} onPress={() => navigation.goBack()}>
        <Ionicons name="arrow-back" size={22} color="#FFF" />
      </TouchableOpacity>

      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Banner */}
        {coverImage ? (
          <Image source={coverImage} style={styles.banner} resizeMode="contain" />
        ) : (
          <View style={[styles.bannerPlaceholder, { backgroundColor: isDarkTheme ? "#1A1F2E" : "#1E293B" }]}>
            <View style={styles.bannerAvatarRow}>
              {characters.slice(0, 4).map((char, idx) => (
                <View
                  key={char.name}
                  style={[styles.bannerAvatarCircle, idx > 0 && styles.bannerAvatarOverlap]}
                >
                  <Image source={char.avatar} style={styles.bannerAvatarImg} resizeMode="cover" />
                </View>
              ))}
            </View>
            {characters.length === 0 && (
              <Ionicons name="images-outline" size={48} color={"rgba(255,255,255,0.3)"} />
            )}
          </View>
        )}

        <View style={styles.body}>
          {/* Title & badge */}
          <View style={styles.titleRow}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.missionTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
              {"subtitle" in mission && mission.subtitle ? (
                <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{mission.subtitle as string}</Text>
              ) : null}
            </View>
            {certification ? (
              <View style={[styles.certBadge, { backgroundColor: colors.primary }]}>
                <Text style={styles.certBadgeText}>{certification}</Text>
              </View>
            ) : null}
          </View>

          {/* Synopsis */}
          <View style={[styles.section, { borderBottomColor: colors.border }]}>
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>Synopsis</Text>
            {synopsisParas.map((para, i) => (
              <Text key={i} style={[styles.synopsisPara, { color: colors.textSecondary }]}>
                {para}
              </Text>
            ))}
          </View>

          {/* Characters */}
          {characters.length > 0 && (
            <View style={[styles.section, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>Personnages</Text>
              <View style={styles.charsGrid}>
                {characters.map((char: StoryCharacter) => (
                  <View key={char.name} style={styles.charItem}>
                    <Image source={char.avatar} style={styles.charAvatar} />
                    <Text style={[styles.charName, { color: colors.textPrimary }]} numberOfLines={1}>
                      {char.name}
                    </Text>
                    <Text style={[styles.charRole, { color: colors.textSecondary }]} numberOfLines={2}>
                      {char.role}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Learning objectives */}
          {objectives.length > 0 && (
            <View style={[styles.section, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>Objectifs d apprentissage</Text>
              {objectives.map((obj, i) => (
                <View key={i} style={styles.objRow}>
                  <Ionicons name="checkmark-circle" size={16} color={colors.success} style={{ marginTop: 2 }} />
                  <Text style={[styles.objText, { color: colors.textSecondary }]}>{obj}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Action buttons */}
          <View style={styles.actions}>
            {isActive && (
              <TouchableOpacity
                style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
                onPress={() => navigation.navigate("ChapterDetail", { missionId, chapterId: 1 })}
              >
                <Ionicons name="play-circle" size={18} color="#FFF" />
                <Text style={styles.primaryBtnText}>Continuer</Text>
              </TouchableOpacity>
            )}
            {isTrial && !isActive && (
              <>
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
                  onPress={() => navigation.navigate("ChapterDetail", { missionId, chapterId: 1 })}
                >
                  <Ionicons name="play-circle" size={18} color="#FFF" />
                  <Text style={styles.primaryBtnText}>Ouvrir le Trial</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.secondaryBtn, { backgroundColor: inCart ? colors.success : colors.secondary }]}
                  onPress={() => { if (!inCart) addToCart(missionId); }}
                  disabled={inCart}
                >
                  <Ionicons name={inCart ? "checkmark" : "cart"} size={18} color="#FFF" />
                  <Text style={styles.primaryBtnText}>{inCart ? "Dans le panier" : "Acheter"}</Text>
                </TouchableOpacity>
              </>
            )}
            {!isActive && !isTrial && (
              <>
                <TouchableOpacity
                  style={[styles.outlineBtn, { borderColor: colors.primary, backgroundColor: inTrial ? colors.success + "22" : "transparent" }]}
                  onPress={() => { if (!inTrial) startTrial(missionId); }}
                  disabled={inTrial}
                >
                  <Ionicons name={inTrial ? "checkmark-circle" : "play"} size={18} color={inTrial ? colors.success : colors.primary} />
                  <Text style={[styles.outlineBtnText, { color: inTrial ? colors.success : colors.primary }]}>
                    {inTrial ? "En Trial" : "Start Trial"}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: inCart ? colors.success : colors.primary }]}
                  onPress={() => { if (!inCart) addToCart(missionId); }}
                  disabled={inCart}
                >
                  <Ionicons name={inCart ? "checkmark" : "cart"} size={18} color="#FFF" />
                  <Text style={styles.primaryBtnText}>{inCart ? "Dans le panier" : "Buy Now"}</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: {
    position: "absolute",
    top: 48,
    left: 16,
    zIndex: 10,
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  banner: { width: "100%" as any, aspectRatio: 21 / 9, maxHeight: 250 },
  bannerPlaceholder: { width: "100%" as any, height: 180, alignItems: "center", justifyContent: "center" },
  bannerAvatarRow: { flexDirection: "row", alignItems: "center", justifyContent: "center" },
  bannerAvatarCircle: { width: 64, height: 64, borderRadius: 32, overflow: "hidden", borderWidth: 3, borderColor: "rgba(255,255,255,0.3)" },
  bannerAvatarOverlap: { marginLeft: -18 },
  bannerAvatarImg: { width: 64, height: 64, borderRadius: 32 },
  body: { padding: 20, paddingBottom: 40 },
  titleRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 20, gap: 12 },
  missionTitle: { fontSize: 26, fontWeight: "bold", marginBottom: 4 },
  subtitle: { fontSize: 14, fontStyle: "italic", lineHeight: 20 },
  certBadge: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8, alignSelf: "flex-start", marginTop: 4 },
  certBadgeText: { color: "#FFF", fontSize: 12, fontWeight: "700" },
  section: { paddingBottom: 20, marginBottom: 20, borderBottomWidth: 1 },
  sectionTitle: { fontSize: 18, fontWeight: "700", marginBottom: 12 },
  synopsisPara: { fontSize: 14, lineHeight: 22, marginBottom: 10 },
  charsGrid: { flexDirection: "row", flexWrap: "wrap", gap: 16 },
  charItem: { alignItems: "center", width: 72 },
  charAvatar: { width: 60, height: 60, borderRadius: 30, marginBottom: 6 },
  charName: { fontSize: 12, fontWeight: "600", textAlign: "center" },
  charRole: { fontSize: 10, textAlign: "center", lineHeight: 14, marginTop: 2 },
  objRow: { flexDirection: "row", gap: 10, marginBottom: 8, alignItems: "flex-start" },
  objText: { flex: 1, fontSize: 14, lineHeight: 20 },
  actions: { flexDirection: "row", gap: 12, flexWrap: "wrap" },
  primaryBtn: { flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "center", paddingVertical: 13, borderRadius: 10, gap: 8 },
  primaryBtnText: { color: "#FFF", fontSize: 14, fontWeight: "700" },
  secondaryBtn: { flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "center", paddingVertical: 13, borderRadius: 10, gap: 8 },
  outlineBtn: { flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "center", paddingVertical: 13, borderRadius: 10, borderWidth: 1.5, gap: 8 },
  outlineBtnText: { fontSize: 14, fontWeight: "700" },
});
